import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MusicNote as MusicIcon,
  PlaylistPlay as PlaylistIcon,
  Lock as PrivateIcon,
  Public as PublicIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { usePlaylists } from "../../hooks/useRedux";
import { Playlist } from "../../store/slices/types";

const Playlists: React.FC = () => {
  const navigate = useNavigate();
  const { playlists, loading, error, deletePlaylist } = usePlaylists();
  const [open, setOpen] = useState(false);
  const [viewingPlaylist, setViewingPlaylist] = useState<Playlist | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  // Filter playlists based on active filter
  const filteredPlaylists = activeFilter
    ? playlists.filter((playlist) => playlist.tags.includes(activeFilter))
    : playlists;

  // Calculate statistics
  const totalPlaylists = playlists.length;
  const publicPlaylists = playlists.filter((p) => p.isPublic).length;
  const privatePlaylists = playlists.filter((p) => !p.isPublic).length;
  const recentPlaylists = playlists.filter(
    (p) => p.createdAt && Date.now() - p.createdAt < 7 * 24 * 60 * 60 * 1000
  ).length;

  const handleFilterClick = (filter: string | null) => {
    setActiveFilter(filter);
  };

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: GridColDef[] = [
    {
      field: "isPublic",
      headerName: "",
      flex: 0.1,
      minWidth: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: params.value ? "#4caf50" : "#ff9800",
          }}
        >
          {params.value ? <PublicIcon /> : <PrivateIcon />}
        </Avatar>
      ),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      renderCell: (params) => (
        <Box>
          <Typography variant='body2' fontWeight='600'>
            {params.value}
          </Typography>
          <Typography variant='caption' color='textSecondary' noWrap>
            {params.row.description?.substring(0, 50)}...
          </Typography>
        </Box>
      ),
    },
    {
      field: "items",
      headerName: "Items",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={`${params.value?.length || 0} items`}
          size='small'
          color='primary'
          variant='outlined'
        />
      ),
    },
    {
      field: "isPublic",
      headerName: "Privacy",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Public" : "Private"}
          size='small'
          color={params.value ? "success" : "warning"}
          variant='outlined'
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title='View Playlist'>
              <ViewIcon />
            </Tooltip>
          }
          label='View'
          onClick={() => handleView(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title='Edit Playlist'>
              <EditIcon />
            </Tooltip>
          }
          label='Edit'
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title='Delete Playlist'>
              <DeleteIcon />
            </Tooltip>
          }
          label='Delete'
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  const handleAdd = () => {
    navigate("/playlists/add");
  };

  const handleEdit = (playlist: Playlist) => {
    const id = playlist._id || playlist.id;
    if (id) {
      navigate(`/playlists/edit/${id}`);
    }
  };

  const handleView = (playlist: Playlist) => {
    setViewingPlaylist(playlist);
    setOpen(true);
  };

  const handleDelete = async (id: string) => {
    const playlist = playlists.find((p) => p.id === id);
    const playlistTitle = playlist?.title || "Unknown Playlist";

    if (
      window.confirm(
        `Are you sure you want to delete "${playlistTitle}"? This action cannot be undone.`
      )
    ) {
      try {
        await deletePlaylist(id);
        toast.success("playlist is deleted");
      } catch (error) {
        console.error("Failed to delete playlist:", error);
        toast.error(
          `❌ Failed to delete playlist: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              Playlists Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              View and manage user-created playlists
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
              },
            }}
          >
            Add Playlist
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  activeFilter === null
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform: activeFilter === null ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === null
                    ? "0 8px 25px rgba(102, 126, 234, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                },
              }}
              onClick={() => handleFilterClick(null)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant='h4' fontWeight='bold'>
                      {totalPlaylists}
                    </Typography>
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total Playlists
                    </Typography>
                  </Box>
                  <PlaylistIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "public" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "public"
                    ? "0 8px 25px rgba(76, 175, 80, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "public"
                    ? "2px solid #4caf50"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(76, 175, 80, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("public")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='success.main'
                    >
                      {publicPlaylists}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Public
                    </Typography>
                  </Box>
                  <PublicIcon sx={{ fontSize: 40, color: "success.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "private" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "private"
                    ? "0 8px 25px rgba(255, 152, 0, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "private"
                    ? "2px solid #ff9800"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(255, 152, 0, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("private")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='warning.main'
                    >
                      {privatePlaylists}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Private
                    </Typography>
                  </Box>
                  <PrivateIcon sx={{ fontSize: 40, color: "warning.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "recent" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "recent"
                    ? "0 8px 25px rgba(33, 150, 243, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "recent"
                    ? "2px solid #2196f3"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(33, 150, 243, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("recent")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='info.main'
                    >
                      {recentPlaylists}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      This Week
                    </Typography>
                  </Box>
                  <CalendarIcon sx={{ fontSize: 40, color: "info.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filter Indicator */}
      {activeFilter && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Showing {activeFilter} playlists:
          </Typography>
          <Chip
            label={activeFilter}
            color='primary'
            size='small'
            onDelete={() => handleFilterClick(null)}
          />
        </Box>
      )}

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={filteredPlaylists}
          columns={columns}
          getRowId={(row) => row._id || row.id || String(Math.random())}
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={filteredPlaylists.length}
          loading={loading}
          autoHeight
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          filterModel={undefined}
          onFilterModelChange={() => {}}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-filterForm": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            filter: {
              filterModel: {
                items: [],
              },
            },
          }}
        />
      </Paper>

      <PlaylistDialog
        open={open}
        onClose={() => setOpen(false)}
        playlist={viewingPlaylist}
      />
    </Box>
  );
};

interface PlaylistDialogProps {
  open: boolean;
  onClose: () => void;
  playlist: Playlist | null;
}

const PlaylistDialog: React.FC<PlaylistDialogProps> = ({
  open,
  onClose,
  playlist,
}) => {
  if (!playlist) return null;

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          p: 3,
          borderRadius: "12px 12px 0 0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
            }}
          >
            <PlaylistIcon />
          </Box>
          <Box>
            <Typography variant='h5' fontWeight='700'>
              {playlist.title}
            </Typography>
            <Typography variant='body2' sx={{ opacity: 0.9 }}>
              {playlist.createdAt ? formatDate(playlist.createdAt) : "N/A"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          {/* Privacy and Items Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(
                      playlist.isPublic ? "#4caf50" : "#ff9800",
                      0.1
                    ),
                    border: `2px solid ${
                      playlist.isPublic ? "#4caf50" : "#ff9800"
                    }`,
                  }}
                >
                  <Typography variant='h6' fontWeight='600' gutterBottom>
                    Privacy
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant='h4' fontWeight='bold'>
                      {playlist.isPublic ? "🌐" : "🔒"}
                    </Typography>
                    <Box>
                      <Typography variant='h6' fontWeight='600'>
                        {playlist.isPublic ? "Public" : "Private"}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {playlist.isPublic
                          ? "Visible to all users"
                          : "Only visible to the creator"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha("#667eea", 0.1),
                    border: `2px solid ${alpha("#667eea", 0.2)}`,
                  }}
                >
                  <Typography variant='h6' fontWeight='600' gutterBottom>
                    Items
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant='h4' fontWeight='bold'>
                      🎵
                    </Typography>
                    <Box>
                      <Typography variant='h6' fontWeight='600'>
                        {playlist.items?.length || 0} items
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Content in this playlist
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Description Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant='h6'
              fontWeight='600'
              gutterBottom
              sx={{
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              📝 Description
            </Typography>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha("#667eea", 0.05),
                border: `1px solid ${alpha("#667eea", 0.2)}`,
                minHeight: 100,
              }}
            >
              <Typography variant='body1' sx={{ lineHeight: 1.6 }}>
                {playlist.description || "No description provided"}
              </Typography>
            </Box>
          </Box>

          {/* Items Section */}
          {playlist.items && playlist.items.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant='h6'
                fontWeight='600'
                gutterBottom
                sx={{
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                🎵 Playlist Items
              </Typography>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: alpha("#667eea", 0.05),
                  border: `1px solid ${alpha("#667eea", 0.2)}`,
                }}
              >
                <Grid container spacing={2}>
                  {playlist.items.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: "white",
                          border: `1px solid ${alpha("#667eea", 0.2)}`,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <MusicIcon color='primary' />
                        <Box>
                          <Typography variant='body2' fontWeight='600'>
                            Item {index + 1}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {item}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          )}

          {/* Tags Section */}
          {playlist.tags && playlist.tags.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant='h6'
                fontWeight='600'
                gutterBottom
                sx={{
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                🏷️ Tags
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {playlist.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    color='secondary'
                    variant='outlined'
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Metadata Section */}
          <Box>
            <Typography
              variant='h6'
              fontWeight='600'
              gutterBottom
              sx={{
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              ℹ️ Playlist Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha("#667eea", 0.05),
                    border: `1px solid ${alpha("#667eea", 0.2)}`,
                  }}
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                  >
                    Creator
                  </Typography>
                  <Typography variant='body1' fontWeight='600'>
                    {playlist.userId}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha("#667eea", 0.05),
                    border: `1px solid ${alpha("#667eea", 0.2)}`,
                  }}
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                  >
                    Created At
                  </Typography>
                  <Typography variant='body1' fontWeight='600'>
                    {playlist.createdAt
                      ? formatDate(playlist.createdAt)
                      : "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha("#667eea", 0.05),
                    border: `1px solid ${alpha("#667eea", 0.2)}`,
                  }}
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                  >
                    Last Updated
                  </Typography>
                  <Typography variant='body1' fontWeight='600'>
                    {playlist.updatedAt
                      ? formatDate(playlist.updatedAt)
                      : "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha("#667eea", 0.05),
                    border: `1px solid ${alpha("#667eea", 0.2)}`,
                  }}
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                  >
                    Color Theme
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 1,
                        backgroundColor: playlist.color,
                        border: "1px solid #ccc",
                      }}
                    />
                    <Typography variant='body1' fontWeight='600'>
                      {playlist.color}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant='outlined'
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Playlists;
